import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { FormGroup, LabeledCheckbox } from '../../../generic/Forms';
import Section from '../../../shared/settings/SettingsPageSection';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { ContainerIssueSettings } from '../../../../../shared/settings/project/issues';

import PropagateContainerModal from './PropagateContainerModal';
import { getEndpoint } from './utils';

/**
 * SettingsOrganizationProjectIssuesContainers is the settings page for
 * managing the default container scanning settings for projects.
 */
const SettingsOrganizationProjectIssuesContainers: VoidFunctionComponent = () => {
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const {
    settings,
    isLoading,
    onUpdateSettings,
    onSaveSettings,
    isSaveButtonDisabled,
  } = useOrganizationSettings<ContainerIssueSettings>({
    endpoint: getEndpoint(window.user?.organizationId as number, 'container'),
    settingsSection: 'project container',
  });
  return (
    <>
      <Section>
        <Section.Left heading="Containers">
          <Section.Paragraph>
            Default behavior of container-based projects.
          </Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton
              onClick={() => setIsPropagateModalOpen(true)}
              isDisabled={!isSaveButtonDisabled}
            />
            <Section.SaveButton
              onClick={onSaveSettings}
              isLoading={isLoading.save}
              isDisabled={isSaveButtonDisabled}
            />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <Section.Heading>
            Base layer
          </Section.Heading>
          <Section.Paragraph>
            Prevent issues from being created if a dependency is used on the base layer.
            {' '}
            This also prevents issues from dependencies used in both the base layer and other container layers.
          </Section.Paragraph>
          <FormGroup spacing="xs">
            <LabeledCheckbox
              label="Exclude licensing issues"
              isChecked={!!settings?.projectDefaultExcludeBaseLayerIssuesLicensing}
              onChange={(_event, projectDefaultExcludeBaseLayerIssuesLicensing) => onUpdateSettings({
                projectDefaultExcludeBaseLayerIssuesLicensing,
              })}
              isRippleDisabled
            />
            <LabeledCheckbox
              label="Exclude security issues"
              isChecked={!!settings?.projectDefaultExcludeBaseLayerIssuesSecurity}
              onChange={(_event, projectDefaultExcludeBaseLayerIssuesSecurity) => onUpdateSettings({
                projectDefaultExcludeBaseLayerIssuesSecurity,
              })}
              isRippleDisabled
            />
            <LabeledCheckbox
              label="Exclude quality issues"
              isChecked={!!settings?.projectDefaultExcludeBaseLayerIssuesQuality}
              onChange={(_event, projectDefaultExcludeBaseLayerIssuesQuality) => onUpdateSettings({
                projectDefaultExcludeBaseLayerIssuesQuality,
              })}
              isRippleDisabled
            />
          </FormGroup>
        </Section.Right>
      </Section>
      {settings && (
        <PropagateContainerModal
          settings={settings}
          isOpen={isPropagateModalOpen}
          onHide={() => setIsPropagateModalOpen(false)}
        />
      )}
    </>
  );
};

/**
 * Container which gates the Containers settings page behind a feature flag
 * check for the container scanning feature.
 */
const SettingsOrganizationProjectIssuesContainersGate: VoidFunctionComponent = () => (
  <SettingsOrganizationProjectIssuesContainers />
);

export default SettingsOrganizationProjectIssuesContainersGate;
